/**
 * Shared mutation key prefix for all automation template editing mutations.
 * Used by useIsMutating to detect when any automation mutation is in-flight,
 * which disables action buttons (Publish, Delete, Discard, etc.) in the editor.
 */
export const AUTOMATION_TEMPLATE_MUTATION_KEY = ['automationTemplate'] as const;

/**
 * Shared mutation scope for all automation step mutations.
 * Mutations sharing this scope are serialized by React Query —
 * each waits for the previous to complete before executing.
 * This prevents race conditions from concurrent step modifications.
 */
export const AUTOMATION_STEP_MUTATION_SCOPE = { id: 'automationStepMutation' } as const;

/**
 * Centralized query keys for automation-related queries.
 * Use these constants to ensure consistency across queries and invalidations.
 */
export const AUTOMATION_QUERY_KEYS = {
	/** Base key for all automation queries */
	all: ['automations'] as const,

	/** List of all automation templates */
	templates: () => [...AUTOMATION_QUERY_KEYS.all, 'templates'] as const,

	/** Single automation template by ID */
	template: (templateId: string) => [...AUTOMATION_QUERY_KEYS.templates(), templateId] as const,

	/** Automation template steps */
	templateSteps: (templateId: string) => [...AUTOMATION_QUERY_KEYS.template(templateId), 'steps'] as const,

	/** Whether this template or any descendant branch template currently has a draft */
	templateDraftState: (templateId: string) => [...AUTOMATION_QUERY_KEYS.template(templateId), 'draft-state'] as const,

	/** Automation on-hold contacts */
	onHold: () => [...AUTOMATION_QUERY_KEYS.all, 'onhold'] as const,

	/** On-hold contacts for a specific template */
	onHoldContacts: (templateId: string) => [...AUTOMATION_QUERY_KEYS.onHold(), templateId] as const,

	/** Automations on hold summary (for nav badge) */
	onHoldSummary: () => [...AUTOMATION_QUERY_KEYS.onHold(), 'summary'] as const,

	/** Contact automations (running automations for a contact) */
	contactAutomations: (contactId: string) => [...AUTOMATION_QUERY_KEYS.all, 'contact', contactId] as const,
} as const;
